import { Route, NavigationGuardNext } from 'vue-router';
import { rolesArray } from '@/lib/jwt';
import { AccountRoles } from '@/models/account';

/**
 * Creates a combined guard function from an array of guards.
 * Guards are executed sequentially. A guard must call the provided next():
 *  - next() with no args: proceeds to the next guard
 *  - next(arg): resolves/aborts navigation immediately (redirect, false, etc.)
 *
 * Supports both synchronous and asynchronous guards. Async guards are awaited
 * before proceeding to the next guard or completing navigation.
 */

type Guard = (to: Route, from: Route, next: NavigationGuardNext) => void | Promise<void>;

const combineGuards = (guards: Guard[]) => {
  return async (to: Route, from: Route, next: NavigationGuardNext) => {
    for (let i = 0; i < guards.length; i += 1) {
      const guard = guards[i];
      let navigationResolved = false;

      const guardNext = (arg?: any) => {
        if (arg !== undefined) {
          navigationResolved = true;
          next(arg);
        }
      };

      await guard(to, from, guardNext);

      if (navigationResolved) {
        return;
      }
    }

    next();
  };
};

/**
 * Checks user roles from JWT data
 * @returns Object with role check results
 */
export const checkJwtRoles = () => {
  const jwtRoles = rolesArray();

  if (!jwtRoles || jwtRoles.length === 0) {
    return {
      hasRoles: false,
      isSitter: false,
      isParent: false,
      isPremium: false,
      isCorporate: false,
      isFamilyConcierge: false,
    };
  }

  const isSitter = jwtRoles.includes(AccountRoles.Sitter as string);
  const isParent = jwtRoles.some((role) =>
    [AccountRoles.Parent, AccountRoles.Premium, AccountRoles.Corporate].includes(role as AccountRoles)
  );
  const isPremium = jwtRoles.includes(AccountRoles.Premium as string);
  const isCorporate = jwtRoles.includes(AccountRoles.Corporate as string);
  const isFamilyConcierge = jwtRoles.includes(AccountRoles.FamilyConcierge as string);

  return {
    hasRoles: true,
    isSitter,
    isParent,
    isPremium,
    isCorporate,
    isFamilyConcierge,
  };
};

export default combineGuards;
